/*=========================================================================================
  File Name: app-routes.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import store from './app-state'

import { authRoutes } from './auth';
import { setupRoutes } from './setup';
import { analyzeRoutes } from './analyze';
import { sandboxRoutes } from './sandbox';
import { optimizerRoutes } from './optimizer';
import { predictorRoutes } from './predictor';
import { competerRoutes } from './competer';
import { onboardRoutes } from './onboard';
import { usersRoutes } from './users';
import { senserRoutes } from './senser';

import appConfig from '@/../appConfig.js';

// const originalPush = Router.prototype.push
// Router.prototype.push = function push (location, onResolve, onReject) {
//   if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
//   try {
//     return originalPush.call(this, location).catch(err => err)
//   } catch (error) {
//     console.log(error)
//   }
// }

Vue.use(Router)

const appRoutes = [

  {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
    path: '',
    component: () => import('@/app/shared/layouts/main/Main.vue'),
    children: [
      // =============================================================================
      // App Routes
      // =============================================================================
      {
        path: '/',
        redirect: (appConfig.config && appConfig.config.landingUrl ? appConfig.config.landingUrl : '/analyze/dashboard')
      },
      // =============================================================================
      // Application Routes
      // =============================================================================
      {
        path: '/orbits/demand',
        name: 'orbitsDemand ',
        component: () => import('@/app/demo/views/DashboardDemand.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'orbits' },
            { title: 'demand', active: true },
          ],
          pageTitle: 'Demand Plan',
          rule: 'editor'
        }
      },
      {
        path: '/orbits/order',
        name: 'order',
        component: () => import('@/app/demo/views/Order.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'orbits' },
            { title: 'order', active: true },
          ],
          pageTitle: 'Channel Order',
          rule: 'editor'
        }
      },
      {
        path: '/orbits/competition',
        name: 'competition',
        component: () => import('@/app/demo/views/Competition.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'orbits' },
            { title: 'competition', active: true },
          ],
          pageTitle: 'Price Monitor',
          rule: 'editor'
        }
      },
      {
        path: '/orbits/sku/:id',
        name: 'sku',
        component: () => import('@/app/demo/views/Sku.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'orbits' },
            { title: 'competition', url: '/orbits/competition' },
            { title: 'stock', url: '/orbits/stock' },
            { title: 'sku', active: true },
          ],
          pageTitle: 'SKU',
          rule: 'editor'
        }
      },
      {
        path: '/orbits/stock',
        name: 'stock',
        component: () => import('@/app/demo/views/Stock.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'orbits' },
            { title: 'competition', url: '/orbits/competition' },
            { title: 'stock', active: true },
          ],
          pageTitle: 'Stock',
          rule: 'editor'
        }
      },
      {
        path: '/pages/profile',
        name: 'pageProfile',
        component: () => import('@/app/demo/views/pages/Profile.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Pages' },
            { title: 'Profile', active: true },
          ],
          pageTitle: 'Profile',
          rule: 'editor'
        },
      },
    // =============================================================================
      // Support Routes
      // =============================================================================
      {
        path: '/pages/faq',
        name: 'pageFAQ',
        component: () => import('@/app/demo/views/pages/Faq.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Pages' },
            { title: 'FAQ', active: true },
          ],
          pageTitle: 'FAQ',
          rule: 'editor'
        },
      },
      {
        path: '/pages/knowledge-base',
        name: 'pageKnowledgeBase',
        component: () => import('@/app/demo/views/pages/KnowledgeBase.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Pages' },
            { title: 'KnowledgeBase', active: true },
          ],
          pageTitle: 'KnowledgeBase',
          rule: 'editor'
        },
      },
      {
        path: '/pages/knowledge-base/category',
        name: 'pageKnowledgeBaseCategory',
        component: () => import('@/app/demo/views/pages/KnowledgeBaseCategory.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Pages' },
            { title: 'KnowledgeBase', url: '/pages/knowledge-base' },
            { title: 'Category', active: true },
          ],
          rule: 'editor'
        },
      },
      {
        path: '/pages/knowledge-base/category/question',
        name: 'pageKnowledgeBaseCategoryQuestion',
        component: () => import('@/app/demo/views/pages/KnowledgeBaseCategoryQuestion.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Pages' },
            { title: 'KnowledgeBase', url: '/pages/knowledge-base' },
            { title: 'Category', url: '/pages/knowledge-base/category' },
            { title: 'Question', active: true },
          ],
          rule: 'editor'
        },
      },
    ]
  },
  // =============================================================================
  // FULL PAGE LAYOUTS
  // =============================================================================
  {
    path: '',
    component: () => import('@/app/shared/layouts/full-page/FullPage.vue'),
    children: [
      // =============================================================================
      // PAGES
      // =============================================================================
      {
        path: '/pages/comingsoon',
        name: 'pageComingSoon',
        component: () => import('@/app/demo/views/pages/ComingSoon.vue'),
        meta: {
          rule: 'editor'
        }
      },
      {
        path: '/pages/error-404',
        name: 'pageError404',
        component: () => import('@/app/demo/views/pages/Error404.vue'),
        meta: {
          rule: 'editor'
        }
      },
      {
        path: '/pages/error-500',
        name: 'pageError500',
        component: () => import('@/app/demo/views/pages/Error500.vue'),
        meta: {
          rule: 'editor'
        }
      },
      {
        path: '/pages/not-authorized',
        name: 'pageNotAuthorized',
        component: () => import('@/app/demo/views/pages/NotAuthorized.vue'),
        meta: {
          rule: 'editor'
        }
      },
      {
        path: '/pages/maintenance',
        name: 'pageMaintenance',
        component: () => import('@/app/demo/views/pages/Maintenance.vue'),
        meta: {
          rule: 'editor'
        }
      }
    ]
  },
  // Redirect to 404 page, if no match found
  {
    path: '*',
    redirect: '/pages/error-404'
  }
];



const routes = [...appRoutes, ...authRoutes, ...setupRoutes, ...analyzeRoutes, ...sandboxRoutes, ...optimizerRoutes, ...predictorRoutes, ...competerRoutes, ...onboardRoutes, ...usersRoutes, ...senserRoutes];

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = "none";
  }
})


router.beforeEach((to, from, next) => {

  Promise.all([store.dispatch('auth/[AUTH] CHECK_AUTH')]).then(() => {

    // get auth current user
    let isAuthenticated = store.getters['auth/isAuthenticated'];
    let currentUser = store.getters['auth/currentUser'];

    if (
      to.path === "/auth/login" ||
      to.path === "/auth/forgot-password" ||
      to.path === "/pages/error-404" ||
      to.path === "/pages/error-500" ||
      to.path === "/auth/register" ||
      to.path === "/auth/callback" ||
      to.path === "/pages/comingsoon" ||
      (isAuthenticated && currentUser)
    ) {
      return next();
    }

    router.push({ path: '/auth/login', query: { to: to.path } })

    // If auth required, check login. If login fails redirect to login page
    // if(to.meta.authRequired) {
    //   if (!(isAuthenticated && currentUser)) {
    //     router.push({ path: '/auth/login', query: { to: to.path } })
    //   }
    // }

    // return next()
    // Specify the current path as the customState parameter, meaning it
    // will be returned to the application after auth
    // auth.login({ target: to.path });
  });
});

/**
 * Async push method
 */
router.pushAsync = function(route) {
  return new Promise((resolve, reject) => {
    router.push(route, resolve, reject);
  });
};

export default router